import type { IOriginalContactState } from 'store/contact/contact.state';
import type { ConnectionType, CallConnection, MonitorInfo, MonitorStatus, CallDirection } from 'store/call/call.state';
import type { IAgent } from '@missionlabs/smartagent-lib-shared/build/agent';

export const getConnectionType = (connection: connect.VoiceConnection): ConnectionType => {
    const type = connection.getType();
    const monitorStatusType = connection.getMonitorStatus();

    const isQuickConnect = connection.getQuickConnectName() !== null;

    const phoneNumber = connection.getAddress().phoneNumber;

    const isOutboundTransfer = type === 'outbound' && phoneNumber !== 'INTERNAL-TRANSFER';

    if (isQuickConnect) return 'QC_TRANSFER';

    if (isOutboundTransfer) return 'OUTBOUND_TRANSFER';

    if (type === 'agent') {
        if (monitorStatusType === 'SILENT_MONITOR') return 'MONITOR';
        if (monitorStatusType === 'BARGE') return 'BARGE';
    }

    if (phoneNumber === 'INTERNAL-TRANSFER') return 'AGENT';

    return 'CUSTOMER';
};

export const getConnectionName = (connection: connect.VoiceConnection, agent?: IAgent): string | undefined => {
    let name: string | undefined;

    if (connection.getStatus().type === connect.ConnectionStateType.CONNECTING) return 'INTERNAL-TRANSFER';

    const type = getConnectionType(connection);

    switch (type) {
        case 'QC_TRANSFER':
            name = connection.getQuickConnectName() ?? 'Unknown';
            break;
        case 'OUTBOUND_TRANSFER':
        case 'CUSTOMER':
            name = connection.getEndpoint().phoneNumber ?? 'Unknown';
            break;
        case 'AGENT':
            name = agent?.username ?? 'Unknown';
            break;
    }

    return name;
};

export const getConnectionStart = (
    connectionType: ConnectionType,
    connectionStatus: connect.ConnectionState,
    callDirection: CallDirection,
    connectedToAgentTimestamp?: string,
    initiationTimestamp?: string
): Date | null => {
    let connectionStart: Date | null = new Date();

    const trackedConnectionTypes = ['QC_TRANSFER', 'OUTBOUND_TRANSFER', 'MONITOR', 'BARGE'];

    if (trackedConnectionTypes.includes(connectionType)) {
        const { type, timestamp } = connectionStatus;

        switch (connectionType) {
            // Set start time to null for transferred connections that are already connected when you monitor
            // The timestamp from the contact is not accurate for transferred connections
            case 'QC_TRANSFER':
                connectionStart = null;
                break;
            case 'OUTBOUND_TRANSFER':
                if (type === connect.ConnectionStateType.CONNECTED || type === connect.ConnectionStateType.HOLD) {
                    connectionStart = null;
                }
                break;
            case 'MONITOR':
            case 'BARGE':
                connectionStart = timestamp;
                break;
        }
    } else {
        let timestamp: string | undefined;

        switch (callDirection) {
            case 'inbound':
                if (connectionType === 'AGENT') timestamp = connectedToAgentTimestamp;
                else if (connectionType === 'CUSTOMER') timestamp = initiationTimestamp;
                break;
            case 'outbound':
                if (connectionType === 'AGENT') timestamp = initiationTimestamp;
                else if (connectionType === 'CUSTOMER') timestamp = connectedToAgentTimestamp;
                break;
        }

        if (timestamp) connectionStart = new Date(timestamp);
    }

    return connectionStart;
};

export const createConnection = (
    connection: connect.VoiceConnection,
    contact?: IOriginalContactState,
    agent?: IAgent
): CallConnection => {
    const connectionType = getConnectionType(connection);
    const connectionStatus = connection.getStatus();

    const { AgentInfo, InitiationTimestamp, InitiationMethod } = contact ?? {};

    return {
        id: connection.getConnectionId(),
        activeConnection: connection.isActive(),
        status: connectionStatus.type,
        start: getConnectionStart(
            connectionType,
            connectionStatus,
            getCallDirection(InitiationMethod),
            AgentInfo?.ConnectedToAgentTimestamp,
            InitiationTimestamp
        ),
        type: connectionType,
        name: getConnectionName(connection, agent),
    };
};

export const updateConnection = (previousConnection: CallConnection, newConnection: CallConnection): CallConnection => {
    const { status: previousStatus, type: previousType, name: previousName } = previousConnection;
    const { status: newStatus, type: newType, name: newName } = newConnection;

    const hasConnected =
        previousStatus === connect.ConnectionStateType.CONNECTING &&
        newStatus === connect.ConnectionStateType.CONNECTED;

    const hasUpdatedToQCTransfer = previousType !== 'QC_TRANSFER' && newType === 'QC_TRANSFER';

    const shouldUpdateName = hasConnected || hasUpdatedToQCTransfer;

    return {
        ...previousConnection,
        name: shouldUpdateName ? newName : previousName,
        type: newType,
        status: newStatus,
    };
};

export const getMonitorInfo = (connection: connect.VoiceConnection): MonitorInfo => {
    let status: MonitorStatus | undefined;
    let isEnhancedMonitoringEnabled: boolean = false;

    if (connection.getType() === connect.ConnectionType.MONITORING) {
        status = 'SILENT_MONITOR';
    } else {
        status = connection.getMonitorStatus();
        if (['SILENT_MONITOR', 'BARGE'].includes(status)) {
            isEnhancedMonitoringEnabled = true;
        }
    }

    return { status, isEnhancedMonitoringEnabled };
};

// Temporary mapper function until we update the type of the direction property to match the initiation method values given by Connect
export const getCallDirection = (initiationMethod: string | undefined): CallDirection => {
    if (!initiationMethod) return 'unknown';

    switch (initiationMethod) {
        case 'INBOUND':
            return 'inbound';
        case 'OUTBOUND':
            return 'outbound';
        case 'TRANSFER':
            return 'transfer';
        case 'QUEUE_TRANSFER':
            return 'queue_transfer';
        case 'CALLBACK':
            return 'callback';
        case 'API':
            return 'api';
        case 'DISCONNECT':
            return 'disconnect';
        case 'MONITOR':
            return 'monitoring';
        case 'EXTERNAL_OUTBOUND':
            return 'external_outbound';
        default:
            return 'unknown';
    }
};
